import { IEquipment } from '../../../../equipment.types.ts'
import liftWidgetSrc from './liftWidget.src.ts'
import liftCardSvgSettings from './liftCard.svgSettings.ts'
import { memo, useMemo } from 'react'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import StandaloneSvgChart from '@/pages/Dashboards/Charts/SVGChart/StandaloneSvgChart'
import useParametersValues from '@/services/data/hooks/useParametersValues.ts'
import useEquipment from '@/pages/Equipment/hooks/useEquipment.ts'
import liftCardConfig from './liftCard.config.ts'
import LiftCardWidget from './LiftCardWidget/index.tsx'
import parametersSelectors from '@/pages/Parameters/parameters.selectors.ts'
import { useRecoilValue } from 'recoil'

interface IProps {
  equipmentId: IEquipment['id']
  live?: boolean
  ts?: number
}

const LiftCard = (props: IProps) => {
  const equipment = useEquipment(props.equipmentId)
  const sourceIdToIdMap = useRecoilValue(parametersSelectors.getSourceIdToIdMap)

  const parametersConfig = useMemo(
    () => liftCardConfig.mapSourceIdsToParameterIds(equipment.protocolId, sourceIdToIdMap),
    [equipment.protocolId, sourceIdToIdMap]
  )

  const parameters = useMemo(
    () =>
      liftCardConfig
        .getSourceIds(equipment.protocolId)
        .map((sourceId) => sourceIdToIdMap[sourceId])
        .filter(Boolean) as Array<IParameter['id']>,
    [equipment.protocolId, sourceIdToIdMap]
  )

  const config = useMemo(
    () => ({
      ts: props.ts,
      live: props.live,
      equipmentId: props.equipmentId,
      parameters,
    }),
    [parameters, props.live, props.ts, props.equipmentId]
  )

  const ctrl = useParametersValues(config)

  const widgetConfig = useMemo(
    () => liftCardConfig.generateWidgetConfig(liftCardSvgSettings(equipment, sourceIdToIdMap)),
    [equipment, sourceIdToIdMap]
  )

  const renderSvgChart = () => {
    return (
      <StandaloneSvgChart
        live={props.live}
        ts={props.ts}
        svgFileSrcString={liftWidgetSrc}
        widget={widgetConfig}
        textParameterPreprocessFn={liftCardConfig.parameterPreprocess}
      />
    )
  }

  return (
    <LiftCardWidget
      chart={renderSvgChart()}
      values={ctrl.values}
      loading={ctrl.loading}
      equipment={equipment}
      live={props.live}
      labels={parametersConfig.labels}
      parameters={parametersConfig.parameters}
    />
  )
}

export default memo(LiftCard)
